import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatError } from '../api/client';
import { Stethoscope, Eye, EyeOff } from 'lucide-react';

const BG_IMAGE = "https://images.pexels.com/photos/14267573/pexels-photo-14267573.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen">
      {/* Left: Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src={BG_IMAGE}
          alt="Cabinet médical"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-emerald-900/40" />
        <div className="absolute bottom-12 left-10 right-10 text-white">
          <h2 className="font-heading text-3xl font-700 mb-2 leading-snug">
            Gestion Médicale<br />Intelligente
          </h2>
          <p className="text-emerald-100 text-base leading-relaxed">
            Dossiers patients, consultations, ordonnances et suivi spécialisé<br />
            pour les médecins généralistes au Maroc.
          </p>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-stone-50 p-8">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 bg-emerald-600 rounded-xl flex items-center justify-center shadow-md">
              <Stethoscope className="text-white" size={22} />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-stone-900">MediConsult</h1>
              <p className="text-xs text-stone-500">Système de Gestion Médicale</p>
            </div>
          </div>

          <h2 className="font-heading text-xl font-semibold text-stone-800 mb-1">Connexion</h2>
          <p className="text-stone-500 text-sm mb-7">Accédez à votre espace médecin</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && (
              <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-stone-700">Adresse email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="medecin@example.com"
                required
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg bg-white text-stone-900 placeholder-stone-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-stone-700">Mot de passe</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-2.5 pr-11 border border-stone-300 rounded-lg bg-white text-stone-900 placeholder-stone-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors duration-150 text-sm"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <p className="text-center text-xs text-stone-400 mt-8">
            © 2024 MediConsult — Maroc. Tous droits réservés.
          </p>
        </div>
      </div>
    </div>
  );
}
